// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Awards.js

import React from 'react';
import { FiAward, FiExternalLink, FiStar } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

// Helper function to resolve the badge source safely
const getImage = (item, apiBaseURL) => {
  const candidate = item.badge || item.image || item.imageUrl || item.logo || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

// Award Card Component
const AwardCard = ({ item, apiBaseURL }) => {
  const badgeSrc = getImage(item, apiBaseURL);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  return (
    <div 
      className="group bg-white rounded-2xl p-6 border transition-all duration-500 hover:shadow-xl hover:-translate-y-1"
      style={{ borderColor: `${primary}15` }}
    >
      <div className="flex items-start gap-4">
        {/* Badge */}
        <div className="flex-shrink-0">
          {badgeSrc ? (
            <img
              src={badgeSrc}
              alt={item.name}
              className="w-16 h-16 object-contain rounded-xl transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
              }}
            >
              <FiAward className="w-8 h-8" style={{ color: secondary }} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" style={{ fontFamily: fontBody }}>
          <h4 
            className="text-lg font-bold mb-1"
            style={{ 
              color: '#1F2937',
              fontFamily: fontHeading,
            }}
          >
            {item.name}
          </h4>
          
          {item.organization && (
            <p className="text-sm" style={{ color: '#6B7280' }}>
              {item.organization}
            </p>
          )}

          {item.year && (
            <p 
              className="text-sm font-medium mt-1"
              style={{ color: primary }}
            >
              {item.year}
            </p>
          )}

          {item.description && (
            <p className="text-sm leading-relaxed mt-3" style={{ color: '#6B7280' }}>
              {item.description}
            </p>
          )}

          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm transition-colors mt-3 group/link"
              style={{ color: secondary }}
            >
              <span>Learn More</span>
              <FiExternalLink className="w-3 h-3 transition-transform group-hover/link:translate-x-0.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Awards Component
const Awards = ({ awards, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, items = [] } = awards || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  return (
    <section id="awards" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium tracking-[0.15em] uppercase mb-4"
                style={{ 
                  color: secondary,
                  backgroundColor: `${secondary}15`,
                  fontFamily: fontBody,
                }}
              >
                <FiStar className="w-3.5 h-3.5" />
                Achievements
              </span>
              
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}

          {/* Awards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.map((item) => (
              <AwardCard 
                key={item._id || item.id || item.name} 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Awards;